/**
 * Global, frontend-only feature flags for hiding in-dev features until they ship.
 *
 * Flags are controlled by the developer through PostHog (ERP admins have no PostHog
 * account, so they neither see nor flip flags). PostHog only initializes when
 * VITE_POSTHOG_KEY is set, which is absent on a local dev machine — so the resolver
 * falls back to a local default in dev, letting the developer see their own
 * in-progress feature without a key.
 *
 * Naming convention: 'dev-<feature>'. Register each flag below with the value it
 * should take locally when PostHog is unavailable (`devDefault`, usually true).
 */

export interface FeatureFlagDefinition {
    /** Value used when PostHog is unavailable AND running in dev mode. */
    devDefault: boolean
}

export const FEATURE_FLAGS = {
    // Canonical example + e2e fixture. Copy this shape for each new in-dev feature.
    'dev-playground': { devDefault: true },
    // KiotViet order mirror: built ahead of the customer approving its quote —
    // unpaid scope stays invisible on prod (unknown flag in PostHog = OFF)
    // until billing is settled. Backend stays dormant separately via
    // KIOTVIET_SYNC_ENABLED.
    'dev-kiotviet-orders': { devDefault: true },
    // MISA e-invoice UI: panels/badges built ahead of the MISA contract being
    // signed. Hidden on prod (unknown flag = OFF) so order/POS screens show no
    // e-invoice surface until rollout. Backend stays dormant separately via
    // EINVOICE_PROVIDER.
    'dev-einvoice': { devDefault: true },
    // Carrier (Viettel Post) dispatch + tracking UI: built while the carrier
    // account is still being activated for production order creation. Hidden on
    // prod (unknown flag = OFF) so order screens show no carrier surface until
    // rollout. The backend stays dormant separately via VTP_ENABLED.
    'dev-carrier-shipping': { devDefault: true },
    // Software digital zoom in the product barcode scanner: a second decode
    // pass over an upscaled centre crop, for phones that cannot focus close
    // enough to fill the frame. It costs CPU on top of the full-frame decoder,
    // so it stays OFF everywhere — including dev — until it is switched on for
    // a specific device (PostHog release condition, or `ff:` in localStorage).
    'dev-scanner-software-zoom': { devDefault: false },
    // In-app "Báo lỗi" header button: hidden on prod (unknown flag = OFF)
    // because staff report bugs in the shared Zalo group instead. Turning the
    // flag on in PostHog brings the button back without a deploy.
    'dev-support-report': { devDefault: true },
} as const satisfies Record<string, FeatureFlagDefinition>

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS

/**
 * Cờ do SERVER cấp qua /auth/me (biến môi trường của bản deploy), không qua
 * PostHog: `server:shop` = SHOP_ENABLED — bật / tắt cả module bán lẻ (menu Bán
 * lẻ, Báo cáo › Website / Zalo Mini App) cùng với API /shop*;
 * `server:zalo-oa` = ZALO_OA_ENABLED — Quản trị → Zalo ZBS cùng API /zalo/*.
 * Dùng được ở `featureFlag` của menu như cờ PostHog, và ở `serverFlag` của
 * entry "Có gì mới".
 */
export const SERVER_FLAG_KEYS = ['server:shop', 'server:zalo-oa'] as const

export type ServerFlagKey = (typeof SERVER_FLAG_KEYS)[number]

export function isServerFlagKey(value: string): value is ServerFlagKey {
    return (SERVER_FLAG_KEYS as readonly string[]).includes(value)
}

/** Khoá cờ của một mục menu: cờ PostHog hoặc cờ server. */
export type NavFlagKey = FeatureFlagKey | ServerFlagKey

export interface ResolveFlagInput {
    /** Whether posthog-js has finished initializing (posthog.__loaded). */
    posthogLoaded: boolean
    /** Raw value from posthog.getFeatureFlag — undefined when the flag is unknown. */
    posthogValue: boolean | string | undefined
    /** Explicit developer override (localStorage `ff:<key>` or VITE_FF_*). */
    localOverride: boolean | undefined
    /** Local fallback value from FEATURE_FLAGS — used only when PostHog is absent in dev. */
    devDefault: boolean
    /** import.meta.env.DEV */
    isDev: boolean
}

/**
 * Pure resolution. Precedence (highest first):
 *   1. localOverride — explicit dev intent, always wins (false means force-OFF).
 *   2. PostHog (once loaded) — source of truth on staging/prod; unknown flag = OFF.
 *   3. dev default — only when PostHog is unavailable and running in dev.
 *   4. fail-safe OFF — PostHog absent and not dev (e.g. prod missing key) → hide.
 */
export function resolveFlag(input: ResolveFlagInput): boolean | string {
    if (input.localOverride !== undefined) return input.localOverride
    if (input.posthogLoaded) return input.posthogValue ?? false
    if (input.isDev) return input.devDefault
    return false
}
